// Flying Fleet Dispatch — Carrier Sourcing & Dispatching Masterclass
// Content adapted from the official Student Slide Deck (10 modules + capstone + game).

export const COURSE_META = {
  title: "Carrier Sourcing & Dispatching Masterclass",
  tagline: "Self-Paced Learning • Live Zoom Practice • Final Exam • Dispatch Game",
  passing: "Recommended passing score: 80%",
};

export const ROADMAP = [
  "Watch the lesson or attend the Zoom review.",
  "Read the matching workbook section.",
  "Complete the worksheet and practice activity.",
  "Review the knowledge check before moving forward.",
  "Use the final capstone to build your own dispatch operating plan.",
];

export const MINDSET = [
  "Represent the carrier clearly and never confuse roles.",
  "Verify facts before accepting, signing, or dispatching anything.",
  "Communicate early when a problem appears.",
  "Document approvals, changes, delays, paperwork, and follow-up.",
  "Think like an operator: safety, service, compliance, and profit all matter.",
];

export const COMPLIANCE = [
  "A dispatch service works for the motor carrier under a written agreement.",
  "The carrier must remain in control of accepting or rejecting loads.",
  "Do not hold yourself out as the broker, carrier, or shipper.",
  "Do not arrange freight in a way that crosses into brokerage without proper authority.",
  "When you are unsure, pause and seek qualified legal review.",
];

export const MODULES = [
  {
    n: 1,
    title: "Trucking Industry Foundations",
    learn: [
      "Map the freight transaction from shipper to receiver.",
      "Understand carrier, broker, shipper, receiver, dispatcher, and factoring roles.",
      "Match equipment to common freight types.",
      "Use dispatch vocabulary accurately.",
    ],
    points: [
      "The freight cycle: Shipper → Broker → Carrier → Dispatcher → Receiver.",
      "Carrier provides the truck, driver, insurance, authority, and transportation.",
      "The dispatcher works FOR the carrier to locate, evaluate, book, and manage loads.",
      "The receiver accepts delivery and signs the proof of delivery (POD).",
      "Never skip verification at any step of the transaction.",
    ],
    quiz: { q: "In the freight transaction, who controls whether a load is accepted?", options: ["The dispatcher", "The carrier", "The broker"], a: 1 },
  },
  {
    n: 2,
    title: "Business Setup & Compliance",
    learn: [
      "Create a professional business foundation.",
      "Understand dispatch-service versus broker boundaries.",
      "Choose basic tools for a secure operation.",
      "Create written policies for pricing, communication, and data.",
    ],
    points: [
      "Choose a legal entity, obtain an EIN, and open a dedicated business bank account.",
      "Set up bookkeeping, business email, and secure cloud storage.",
      "Always use a written agreement with each carrier.",
      "Do not represent yourself as the motor carrier or collect freight charges without guidance.",
      "Pricing models: percentage of gross, flat fee per load, weekly fee, or service package.",
    ],
    quiz: { q: "Which free federal tax ID should the business obtain from the IRS?", options: ["MC Number", "EIN", "DUNS"], a: 1 },
  },
  {
    n: 3,
    title: "Carrier Sourcing & Qualification",
    learn: [
      "Build an ethical carrier-sourcing pipeline.",
      "Verify identity, authority, insurance, and fit.",
      "Conduct a structured discovery call.",
      "Avoid fraud and poor-fit onboarding.",
    ],
    points: [
      "Define an Ideal Carrier Profile: equipment, home base, radius, lanes, experience.",
      "Qualification workflow: Identity → Authority → Safety → Insurance → Equipment.",
      "Check USDOT/MC status through official FMCSA systems.",
      "Red flags: mismatched contacts, refusal to verify, altered insurance, banking pressure.",
      "Never scrape or misuse private information.",
    ],
    quiz: { q: "A carrier refuses to verify authority and pressures you to use new banking info. You should:", options: ["Book quickly before losing the load", "Pause, document, and verify through official sources", "Ask another dispatcher to book it"], a: 1 },
  },
  {
    n: 4,
    title: "Carrier Onboarding & Agreements",
    learn: [
      "Collect a complete carrier file.",
      "Understand the dispatch agreement before signature.",
      "Set communication and load-approval rules.",
      "Launch a carrier with a 7-day onboarding plan.",
    ],
    points: [
      "Standard file: signed agreement, W-9, insurance certificate, authority evidence.",
      "Add a contact sheet, equipment profile, factoring info, and emergency contacts.",
      "Define scope of services and fee terms; confirm the carrier controls acceptance.",
      "Authorization must be written, narrow, revocable, and legal — never sign beyond it.",
      "7-Day Launch: verify → equipment/lanes → board setup → test searches → first booking.",
    ],
    quiz: { q: "A dispatcher's authorization to sign paperwork should be:", options: ["Broad and permanent", "Written, narrow, and revocable", "Verbal and flexible"], a: 1 },
  },
  {
    n: 5,
    title: "Load Boards & Freight Evaluation",
    learn: [
      "Search load boards efficiently.",
      "Read load details critically.",
      "Calculate operational fit before calling.",
      "Create a daily search rhythm.",
    ],
    points: [
      "Build a search profile: equipment, origin radius, preferred lanes, dates, max weight.",
      "Read every detail: miles, weight, commodity, dimensions, temperature, tarping, permits.",
      "Missing details become questions BEFORE booking.",
      "Calculate true miles = loaded miles + realistic deadhead.",
      "Evaluate the broker's authority, payment history, and factoring approval first.",
    ],
    quiz: { q: "Which mileage figure gives the most accurate trip comparison?", options: ["Loaded miles only", "All-in miles (loaded + deadhead)", "Posted miles"], a: 1 },
  },
  {
    n: 6,
    title: "Rate Analysis & Negotiation",
    learn: [
      "Calculate a carrier-specific target rate.",
      "Negotiate from facts instead of emotion.",
      "Use professional scripts.",
      "Document all agreed accessorials.",
    ],
    points: [
      "Know the carrier's cost floor: fuel, driver pay, maintenance, insurance, payments, profit.",
      "Rate metrics: Loaded RPM, All-in RPM, revenue per day, net estimate.",
      "Negotiation: Confirm details → State capacity → Present rate → Respond → Document.",
      "Confirm accessorials: detention, layover, TONU, lumper, tarp, stop-off, permits.",
      "Get a revised rate confirmation in writing before any movement.",
    ],
    quiz: { q: "What must you know BEFORE negotiating a rate?", options: ["The broker's mood", "The carrier's cost floor", "The weather forecast"], a: 1 },
  },
  {
    n: 7,
    title: "Dispatch Operations & Trip Planning",
    learn: [
      "Build a safe, realistic trip plan.",
      "Manage check calls without micromanaging.",
      "Handle service failures professionally.",
      "Protect driver hours and appointment commitments.",
    ],
    points: [
      "Pre-trip: confirm addresses, time zones, appointment times, and required documents.",
      "Never pressure drivers to violate Hours of Service (HOS) or falsify logs.",
      "Check-call rhythm: Dispatched → At Shipper → Loaded → In Transit → Delivered.",
      "Exceptions (detention, breakdown, weather): notify early, document, request written instructions.",
      "Do not dispatch when the facts do not match the rate confirmation.",
    ],
    quiz: { q: "A shipper delay means the driver may run out of hours. The dispatcher should:", options: ["Tell the driver to keep driving anyway", "Adjust the plan and never pressure an HOS violation", "Ignore it and hope for the best"], a: 1 },
  },
  {
    n: 8,
    title: "Documents, Billing & Recordkeeping",
    learn: [
      "Understand core freight documents.",
      "Audit paperwork before submission.",
      "Create an organized billing workflow.",
      "Retain records securely.",
    ],
    points: [
      "Audit the rate confirmation: carrier legal name, MC number, dates, locations, rate.",
      "The BOL documents the shipment; the signed delivery becomes the POD.",
      "Invoice package: carrier invoice, signed BOL/POD, rate confirmation, and receipts.",
      "All verbal changes must be shown in writing before dispatch.",
      "Use secure folders, naming rules, least-privilege access, MFA, and backups.",
    ],
    quiz: { q: "Which document proves a load was delivered and signed for?", options: ["W-9", "POD (Proof of Delivery)", "EIN letter"], a: 1 },
  },
  {
    n: 9,
    title: "Sales, Marketing & Carrier Retention",
    learn: [
      "Create a clear, compliant offer.",
      "Generate leads through trust-building content.",
      "Run sales calls without unrealistic promises.",
      "Retain carriers through measurable service.",
    ],
    points: [
      "Choose a niche: equipment type, region, new authority, owner-operators, or specialized freight.",
      "Describe the problem you solve instead of promising guaranteed revenue.",
      "Sales flow: Discover → Diagnose → Explain → Support → Recover.",
      "Provide weekly performance recaps and respectful communication.",
      "Avoid guaranteed income claims, pressure tactics, and fake testimonials.",
    ],
    quiz: { q: "When marketing your dispatch service, you should:", options: ["Promise guaranteed weekly income", "Describe the operational problem you solve", "Use fake testimonials to build trust"], a: 1 },
  },
  {
    n: 10,
    title: "Scaling, KPIs & Final Capstone",
    learn: [
      "Measure performance using operational KPIs.",
      "Create SOPs before hiring.",
      "Scale without losing compliance or service quality.",
      "Complete a capstone operating plan.",
    ],
    points: [
      "Track KPIs: gross revenue per truck, all-in RPM, deadhead %, on-time %, retention.",
      "Write SOPs for onboarding, load search, verification, negotiation, dispatch, and billing.",
      "Scale slowly — protect compliance and service quality as you grow.",
      "Capstone: niche, services, compliance boundaries, pricing, workflows, KPIs, 90-day plan.",
      "Your capstone should be clear enough for another trained person to follow.",
    ],
    quiz: { q: "Which is a healthy dispatch KPI to track?", options: ["Office square footage", "All-in RPM and carrier retention", "Number of coffees per day"], a: 1 },
  },
];

export const ROLES = [
  { role: "Carrier", resp: "Transports freight under its own authority.", remember: "Carrier controls load acceptance." },
  { role: "Broker", resp: "Arranges freight between shipper and carrier.", remember: "Do not claim to be the broker." },
  { role: "Dispatcher", resp: "Supports the carrier administratively.", remember: "Act only within authorization." },
  { role: "Factoring Co.", resp: "Buys eligible invoices; checks broker credit.", remember: "Know carrier restrictions." },
  { role: "Receiver", resp: "Accepts freight at delivery.", remember: "POD must be clear and signed." },
];

export const EQUIPMENT = [
  { type: "Dry Van", fit: "Packaged general freight.", watch: "Trailer condition & requirements." },
  { type: "Reefer", fit: "Temperature-controlled freight.", watch: "Set point, mode, washout, fuel." },
  { type: "Flatbed", fit: "Machinery, steel, building materials.", watch: "Securement, tarps, dimensions." },
  { type: "Step Deck", fit: "Taller open-deck freight.", watch: "Deck height, ramps, loading." },
  { type: "Lowboy", fit: "Heavy or tall machinery.", watch: "Axles, permits, routes, escorts." },
  { type: "Box Truck", fit: "Local, expedited, partial freight.", watch: "Payload, dock height, liftgate." },
  { type: "Hotshot", fit: "Smaller time-sensitive loads; pickup + gooseneck.", watch: "Payload, rating, securement, permits." },
];

export const GLOSSARY = [
  { t: "Deadhead", d: "Empty miles driven before or after a load." },
  { t: "Linehaul", d: "Transportation charge before accessorials." },
  { t: "Detention", d: "Delay time / fee at a shipper or receiver." },
  { t: "Layover", d: "A longer delay that prevents normal movement." },
  { t: "TONU", d: "Truck Ordered Not Used — fee when a booked load is canceled." },
  { t: "BOL", d: "Bill of Lading — legal document listing the freight." },
  { t: "POD", d: "Proof of Delivery — signed confirmation of delivery." },
  { t: "Rate Con", d: "Rate Confirmation — the agreed rate & terms for a load." },
  { t: "RPM", d: "Rate Per Mile — total pay divided by miles." },
  { t: "MC Number", d: "Motor Carrier operating authority from the FMCSA." },
  { t: "USDOT #", d: "Federal registration number identifying a carrier." },
  { t: "Reefer", d: "Refrigerated trailer for temperature-controlled freight." },
  { t: "Hotshot", d: "Pickup + gooseneck trailer for smaller, urgent loads." },
  { t: "Factoring", d: "Selling invoices to get paid quickly instead of net-30." },
  { t: "Lumper", d: "A third-party service (fee) that loads/unloads freight." },
  { t: "HOS", d: "Hours of Service — federal limits on driver time." },
];

export const KPIS = [
  "Gross revenue per truck", "All-in RPM", "Deadhead percentage",
  "Booked loads per search hour", "On-time pickup / delivery", "Detention recovered",
  "Document turnaround", "Carrier retention", "Complaint rate", "Fee collection rate",
];

export const CAPSTONE = [
  "Niche + services", "Compliance boundaries", "Pricing model", "Sourcing plan",
  "Onboarding workflow", "Daily dispatch workflow", "Negotiation policy", "Document workflow",
  "Data security", "KPIs", "90-day launch calendar", "Legal review needs",
];

export const EXAM_GUIDE = [
  "Review module vocabulary: MC, DOT, BOL, POD, TONU, detention, layover, RPM, deadhead.",
  "Know the difference between dispatcher, carrier, broker, shipper, receiver, and factoring company.",
  "Explain onboarding, load evaluation, negotiation, dispatch, paperwork, and closeout.",
  "Practice the final scenario: onboarding a carrier through delivery and file closeout.",
];

export const LLC_STEPS = [
  { t: "Choose your business name", d: "Search your state database and confirm the name is available." },
  { t: "Choose a registered agent", d: "This person or company receives official mail and legal notices." },
  { t: "File Articles of Organization", d: "File with your state agency and pay the filing fee." },
  { t: "Create an operating agreement", d: "Write ownership, management, banking, and decision rules." },
  { t: "Apply for an EIN", d: "Apply directly through the IRS — EINs are free." },
  { t: "Open business accounts", d: "Separate banking, bookkeeping, contracts, and records." },
];

// Equipment → freight matching game
export const GAME_ITEMS = [
  { term: "Reefer", match: "Refrigerated freight" },
  { term: "Flatbed", match: "Open deck / oversized" },
  { term: "Dry Van", match: "Enclosed general freight" },
  { term: "Hotshot", match: "Pickup + gooseneck, urgent" },
  { term: "Lowboy", match: "Heavy / tall machinery" },
];

// Dispatch Control Room decision challenge
export const SCENARIOS = [
  {
    round: "Carrier Verification",
    situation: "A new carrier gives you an insurance certificate, but the company name doesn't match the authority on file and they won't get on a verification call.",
    options: [
      { label: "Pause & Verify", correct: true },
      { label: "Accept", correct: false },
      { label: "Book anyway", correct: false },
    ],
    explain: "Mismatched identity + refusal to verify are red flags. Pause the workflow, document the concern, and verify through official sources.",
  },
  {
    round: "Rate Negotiation",
    situation: "A broker offers a rate that is $0.15/mi below the carrier's stated cost floor and pressures you to book now.",
    options: [
      { label: "Decline / Counter", correct: true },
      { label: "Accept", correct: false },
      { label: "Book below floor", correct: false },
    ],
    explain: "Never force a load below the carrier's cost floor. Decline or counter with facts (miles, timing, deadhead, market).",
  },
  {
    round: "Paperwork & Billing",
    situation: "A rate confirmation arrives with the wrong MC number and a delivery date different from what you discussed.",
    options: [
      { label: "Correct in Writing", correct: true },
      { label: "Dispatch as-is", correct: false },
      { label: "Sign and fix later", correct: false },
    ],
    explain: "Discrepancies stop dispatch. Request a corrected rate confirmation in writing before signing or moving the load.",
  },
  {
    round: "Exception Handling",
    situation: "A shipper delay means the driver will run out of legal hours before the delivery appointment.",
    options: [
      { label: "Adjust Plan / Notify", correct: true },
      { label: "Tell driver to push through", correct: false },
      { label: "Ignore it", correct: false },
    ],
    explain: "Never pressure an HOS violation. Notify the correct parties early, document facts, and rebuild a safe, legal trip plan.",
  },
];

export const FB_TEMPLATES = [
  {
    name: "Educational Post",
    body: "Owner-operators: Before accepting a load, confirm deadhead, pickup/delivery appointments, commodity, weight, accessorial terms, and broker payment details. Flying Fleet Dispatch LLC helps carriers stay organized and evaluate freight professionally. DM \"DISPATCH\" with your equipment type and preferred lanes.",
  },
  {
    name: "Carrier Fit Post",
    body: "Now speaking with qualified carriers who need dispatch support. Best fit: carriers with active authority, proper insurance, clear equipment specs, and professional communication. Comment your equipment type or message us to schedule a discovery call.",
  },
];

// Standalone "Run Your Dispatch Day" simulation loads
export const SIM_LOADS = [
  { lane: "Chicago, IL → Columbus, OH", equipment: "Dry Van", miles: 355, deadhead: 20, rate: 780, note: "Reputable broker, clean rate con, appointments confirmed.", trap: false },
  { lane: "Dallas, TX → Little Rock, AR", equipment: "Reefer", miles: 320, deadhead: 60, rate: 540, note: "Rate is below your cost floor and the broker won't confirm detention terms.", trap: true },
  { lane: "Atlanta, GA → Nashville, TN", equipment: "Flatbed", miles: 250, deadhead: 15, rate: 720, note: "Solid RPM, tarps required, securement understood.", trap: false },
  { lane: "Phoenix, AZ → Los Angeles, CA", equipment: "Hotshot", miles: 375, deadhead: 90, rate: 610, note: "High deadhead eats the margin; appointment may exceed driver hours.", trap: true },
  { lane: "Kansas City, MO → Denver, CO", equipment: "Dry Van", miles: 600, deadhead: 25, rate: 1320, note: "Strong lane, factoring-approved broker, backhaul available.", trap: false },
];
